import type { ClientConfig } from '@/client_config';
import type { ApiMap, ApiNameType } from '@/constants';
import { isCloudApisUrlKey, isCloudOnlyApiKey } from '@/utils/api.ts';

/** Resolved base required for fetch; throws if {@link useGetApiUrl} returned undefined (portable, no site). */
export const assertApiBaseForFetch = (api: string | undefined): string => {
  if (api == null) {
    throw new Error('API base URL is not resolved. Select a site when using portable mode.');
  }
  return api;
};

/** Remove trailing slashes (keep protocol slashes) */
const normalize = (url?: string): string | undefined => {
  const n = url?.replace(/\/+$/, '') ?? '';
  return n || undefined;
};

type ResolveApiBaseUrlFromSourcesArgs = {
  baseApiRoute: ApiNameType;
  currentSiteApis: ApiMap | undefined;
  defaultSiteApis: ApiMap | undefined;
  portableMode: boolean | undefined;
  clientApis: ClientConfig['apis'];
};

/**
 * Pure resolution used by {@link useGetApiUrl}.
 *
 * Returns `undefined` only when portable mode has no selected site (`currentSiteApis` missing) and
 * the route is not a hosted cloud key — edge bases are unknown until a site is chosen. Callers must
 * disable queries or guard mutations when the result is missing.
 *
 * Otherwise throws (after `console.error`) when no base URL can be resolved so callers never
 * concatenate an empty string into `fetch` URLs.
 */
export const resolveApiBaseUrlFromSources = ({
  baseApiRoute,
  currentSiteApis,
  defaultSiteApis,
  portableMode,
  clientApis,
}: ResolveApiBaseUrlFromSourcesArgs): string | undefined => {
  const fromContext = normalize(currentSiteApis?.[baseApiRoute]);
  if (fromContext) return fromContext;

  const fromDefaultSite = normalize(defaultSiteApis?.[baseApiRoute]);
  if (fromDefaultSite) return fromDefaultSite;

  if (isCloudApisUrlKey(baseApiRoute) && (portableMode || isCloudOnlyApiKey(baseApiRoute))) {
    const raw = clientApis[baseApiRoute];
    const fromClient = normalize(typeof raw === 'string' ? raw.trim() : undefined);
    if (fromClient) return fromClient;
  }

  if (portableMode && !isCloudApisUrlKey(baseApiRoute) && currentSiteApis === undefined) {
    return undefined;
  }

  const msg = [
    `Cannot resolve API base URL for "${baseApiRoute}".`,
    'Check runtime /config (default site, env), site inventory APIs, portableMode, and bundled client_config for cloud routes.',
  ].join(' ');
  console.error(msg);
  throw new Error(msg);
};
